/**
 * PI Lab の画面の識別子。改善要望の「どこの話か」に使う。
 *
 * **フリーテキストにしない。** 表記がばらつくと（「要件定義」「要件定義画面」「spec」）、
 * 同じ場所への要望が別物として溜まり、AI は重複を判定できず、直す場所も特定できない。
 *
 * 一覧は 2 つに分かれている。
 *
 * - `PILAB_NAV_AREA_PATHS`: サイドバーのメニューと 1 対 1。値はナビゲーションのキー
 *   （`useNavGroups.tsx` の `NavDef.key`）と**同じもの**で、表示名は既に i18n の
 *   `nav.<key>` にある。ずれていないことは web 側のテストが見る（`nav-areas.test.ts`）。
 * - `PILAB_EXTRA_AREA_PATHS`: **メニューに無い場所。** 他の画面の中にあるタブや、
 *   どの画面でもない横断の話がここに入る。要望の対象をメニューだけに縛ると、画面が
 *   案件詳細のタブへ移った瞬間に**その機能への要望を出す先が消える**（移したことに
 *   気付かないまま、要望が別の画面に寄せられる）。表示名は `improvements.areas.<key>`。
 *
 * `path` を持たせているのは、**AI が直す場所を探す起点**にするため。要望の画面キーから
 * どのルートの話かが決まるので、コードを当てずっぽうに探さなくてよい。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 改善要望の対象画面を指定するための、PI Lab 画面の安定した識別子
 * @graph-connects none
 */
/** サイドバーのメニューと 1 対 1 の画面。 @graph-connects none */
export const PILAB_NAV_AREA_PATHS = {
    dashboards: '/dashboards',
    kpi: '/kpi',
    reliability: '/reliability',
    qa: '/qa',
    engineerBoard: '/members/engineer-board',
    idtKpi: '/members/idt-kpi',
    piDivManagement: '/pi-div-management',
    projects: '/projects',
    allPjBoard: '/projects/all-pj-board',
    pjSandbox: '/projects/pj-sandbox',
    specRequirements: '/projects/spec',
    basicDesign: '/projects/bd',
    pmoBoard: '/projects/pmo-board',
    assignmentHistory: '/projects/assignment-history',
    lightTasks: '/projects/light-tasks',
    acDivBoard: '/projects/ac-div-board',
    mallDivBoard: '/projects/mall-div-board',
    logiDivBoard: '/projects/logi-div-board',
    accounts: '/accounts',
    teamAssignments: '/team-assignments',
    bugEvaluation: '/bug-evaluation',
    qaResponsibility: '/qa-responsibility',
    engineerEvaluation: '/engineer-evaluations',
    dbDictionary: '/db-dictionary',
    knowledgeWiki: '/knowledge-wiki',
    ccUsage: '/cc-usage',
    ccCommandMatrix: '/cc-command-matrix',
    mcpUsage: '/mcp-usage',
    sreManagement: '/sre-management',
    operations: '/ops',
    improvements: '/improvements',
    orgIssues: '/org-issues',
    teamMeetings: '/team-meetings',
    cuttysark: '/cuttysark',
};
/** メニューに無い場所。タブや横断の話。 @graph-connects none */
export const PILAB_EXTRA_AREA_PATHS = {
    /**
     * どの画面でもない話（横断・全体の使い勝手・入口の分かりにくさなど）。
     *
     * これが無いと、画面に紐づかない要望を無理にどこかの画面へ寄せることになり、
     * **その画面の要望として数えられてしまう。**
     */
    whole: '/',
};
/** 画面の識別子と、その画面の入口。 @graph-connects none */
export const PILAB_AREA_PATHS = {
    ...PILAB_NAV_AREA_PATHS,
    ...PILAB_EXTRA_AREA_PATHS,
};
/** 画面の識別子の一覧（スキーマの enum 生成用）。 @graph-connects none */
export const PILAB_AREAS = Object.keys(PILAB_AREA_PATHS);
/** メニューに無い場所の一覧。表示名を `nav.<key>` から引けない側。 @graph-connects none */
export const PILAB_EXTRA_AREAS = Object.keys(PILAB_EXTRA_AREA_PATHS);
/** 知っている画面かどうか。 @graph-connects none */
export function isPilabArea(value) {
    return value in PILAB_AREA_PATHS;
}
/** メニューに出る画面かどうか（表示名を `nav.<key>` から引けるか）。 @graph-connects none */
export function isPilabNavArea(value) {
    return value in PILAB_NAV_AREA_PATHS;
}
//# sourceMappingURL=pilab-area.js.map